import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface DashboardProps {
  onLogout?: () => void;
}

function Dashboard({ onLogout }: DashboardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);

  return (
    <div className="container mx-auto p-8">
      <header className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-bold text-blue-400 mb-2">
            {t('admin:dashboard.title')}
          </h1>
          <p className="text-gray-400">
            {t('admin:dashboard.subtitle')}
          </p>
        </div>
        {onLogout && (
          <button
            onClick={onLogout}
            className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-4 py-2 rounded-lg transition text-sm"
          >
            Logout
          </button>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Session Management */}
        <Link to="/admin/sessions">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-green-500 transition cursor-pointer">
            <h2 className="text-xl font-semibold mb-4 text-green-400">
              {t('admin:sessions.title')}
            </h2>
            <p className="text-gray-400 mb-4">
              {t('admin:sessions.subtitle')}
            </p>
            <div className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition text-center">
              {t('admin:sessions.manage')}
            </div>
          </div>
        </Link>

        {/* Character Sheets */}
        <Link to="/admin/sheets">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-purple-500 transition cursor-pointer">
            <h2 className="text-xl font-semibold mb-4 text-purple-400">
              {t('admin:characterSheets.title')}
            </h2>
            <p className="text-gray-400 mb-4">
              {t('admin:characterSheets.subtitle')}
            </p>
            <div className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded transition text-center">
              {t('admin:characterSheets.library')}
            </div>
          </div>
        </Link>

        {/* TODO: Votazioni, Sfide, Controllo Proiezione nascosti per ora (placeholder) */}

        {/* QR Code Sala */}
        <div className="bg-gray-800 rounded-lg p-6 border border-purple-500">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl">🎫</span>
            <h2 className="text-xl font-semibold text-white">{t('admin:qrCodes.title')}</h2>
          </div>
          <p className="text-gray-400 mb-6">
            {t('admin:qrCodes.subtitle')}
          </p>
          <button
            onClick={() => navigate('/admin/qr-codes')}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition"
          >
            📥 {t('admin:qrCodes.viewAndDownload')}
          </button>
          <p className="text-sm text-gray-500 text-center mt-3">
            {t('admin:qrCodes.teamCount')}
          </p>
        </div>

        {/* Settings */}
        <Link to="/admin/settings">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-indigo-500 transition cursor-pointer">
            <h2 className="text-xl font-semibold mb-4 text-indigo-400">
              {t('admin:settings.title')}
            </h2>
            <p className="text-gray-400 mb-4">
              {t('admin:settings.subtitle')}
            </p>
            <div className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded transition text-center">
              {t('admin:settings.title')}
            </div>
          </div>
        </Link>
      </div>

      {/* Status Bar */}
      <div className="mt-8 bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-lg font-semibold mb-4">{t('admin:systemStatus.title')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-gray-400 text-sm">{t('admin:systemStatus.activeSession')}</p>
            <p className="text-xl font-bold text-green-400">{t('admin:systemStatus.none')}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">{t('admin:systemStatus.connectedAudience')}</p>
            <p className="text-xl font-bold text-blue-400">0</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">{t('admin:systemStatus.displayStatus')}</p>
            <p className="text-xl font-bold text-yellow-400">{t('admin:systemStatus.disconnected')}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">{t('admin:systemStatus.server')}</p>
            <p className="text-xl font-bold text-green-400">{t('admin:systemStatus.connected')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
